import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Stethoscope } from 'lucide-react';
import { useStep } from '@/hooks/useStep';
import { useDebounce } from '@/hooks/useDebounce';
import {
	advanceSessionWizardStep,
	decodeUrl,
	encodeUrl,
	formatProfessionalFullName,
	getSessionWizardListPanelKey,
} from '@/utils';
import { toast } from 'react-toastify';

type WizardTableEncoded = {
	s?: string | number;
	p?: string;
	professional_id?: string;
	service_id?: string;
};

import {
	useGetProfessional,
	useGetProfessionals,
	useGetServices,
} from '@/services';

import {
	DataTable,
	Filter,
	FilterOption,
	ProfessionalColumnsSession,
	SearchInput,
	SessionStepTitle,
	StepButtonContainer,
} from '@/components';

const baseFilters: Array<FilterOption> = [
	{
		type: 'authorized',
		label: 'Autorizado',
		options: [
			{ value: 'true', label: 'Sim (autorizado)' },
			{ value: 'false', label: 'Não (autorizado)' },
		],
	},
	{
		type: 'contract',
		label: 'Tipo de contrato',
		options: [
			{ value: 'INDEPENDENT', label: 'Independente' },
			{ value: 'PARTNER', label: 'Parceria' },
		],
	},
];

const StepProfessionalTable = () => {
	const professionalsContainerRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const { nextStep, previousStep } = useStep();
	const [searchParams, setSearchParams] = useSearchParams();
	const [professionalSearch, setProfessionalSearch] = useState('');
	const debouncedProfessionalSearch = useDebounce(professionalSearch, 300);
	const listPanelKey = getSessionWizardListPanelKey(
		searchParams,
		'professional',
	);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [selectedProfessionalId, setSelectedProfessinalId] = useState<
		string | null
	>(null);
	const [selectedData, setSelectedData] =
		useState<Partial<Professionals.Professional> | null>(null);
	const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
		null,
	);

	const { data: servicesData } = useGetServices();
	const services = (servicesData ?? []).filter(
		(service) => service.type === 'PROFESSION',
	);

	const filters: Array<FilterOption> = useMemo(() => {
		const modalityFilter: FilterOption = {
			type: 'service_modality',
			label: 'Modalidade',
			options: [
				{ value: 'FULL_REMOTE', label: 'Online' },
				{ value: 'HYBRID', label: 'Híbrido' },
				{ value: 'FULL_PRESENCIAL', label: 'Presencial' },
			],
		};

		const serviceFilter: FilterOption | null =
			services.length > 0
				? {
						type: 'service_id',
						label: 'Serviço',
						options: services.map((service) => ({
							value: service.id,
							label:
								service.professional_label ||
								service.service_name,
						})),
				  }
				: null;

		return serviceFilter
			? [...baseFilters, serviceFilter, modalityFilter]
			: [...baseFilters, modalityFilter];
	}, [services]);

	const serviceModalityParams = searchParams.get(
		'service_modality',
	) as Professionals.ServiceModality;
	const contractParams = searchParams.get(
		'contract',
	) as Professionals.Contract;
	const authorizedParams = searchParams.get('authorized') as 'true' | 'false';
	const serviceIdParams = searchParams.get('service_id') as string; // NOVO ESTADO

	const readWizardState = (): WizardTableEncoded =>
		decodeUrl<WizardTableEncoded>(searchParams.get('d') || '');

	const {
		data: professionalsData,
		isFetching,
		isError,
	} = useGetProfessionals({
		page,
		limit,
		...(debouncedProfessionalSearch && {
			search: debouncedProfessionalSearch,
		}),
		...(serviceModalityParams && {
			service_modality: serviceModalityParams,
		}),
		...(contractParams && { contract: contractParams }),
		...(authorizedParams && { authorized: authorizedParams }),
		service_id: serviceIdParams, // NOVO PARÂMETRO
	});

	const { data: professionalData } = useGetProfessional(
		selectedProfessionalId as string,
		{
			enabled: !!selectedProfessionalId,
		},
	);

	useEffect(() => {
		if (page !== 1) handlePageChange(1);
	}, [
		serviceModalityParams,
		contractParams,
		authorizedParams,
		serviceIdParams,
		debouncedProfessionalSearch,
	]);

	useEffect(() => {
		const encodedUrl = searchParams.get('d');

		if (encodedUrl) {
			const decodedUrl = decodeUrl<WizardTableEncoded>(encodedUrl);

			if (typeof decodedUrl?.professional_id === 'string') {
				setSelectedProfessinalId(decodedUrl.professional_id);
			}

			if (typeof decodedUrl?.service_id === 'string') {
				setSelectedServiceId(decodedUrl.service_id);
			}

			const newEncodedUrl = encodeUrl({
				...decodedUrl,
				s: '2',
			});

			searchParams.set('d', newEncodedUrl);
			navigate(`?${searchParams}`, { replace: true });
		}
	}, []);

	useEffect(() => {
		if (professionalData && !selectedData) {
			setSelectedData({
				first_name: professionalData?.first_name || '',
				last_name: professionalData?.last_name || '',
			});
		}
	}, [professionalData]);

	const handleProfessionalSelected = async (
		professional: Professionals.Professional,
	) => {
		// Toggle: se já estiver selecionado, desmarca; caso contrário, seleciona.
		const isAlreadySelected = selectedProfessionalId === professional.id;

		if (isAlreadySelected) {
			setSelectedProfessinalId(null);
			setSelectedServiceId(null);
			setSelectedData(null);

			// Mantém demais dados e apenas limpa professional_id/service_id.
			const encodedUrl = searchParams.get('d');
			let decoded: WizardTableEncoded | undefined;
			if (encodedUrl) decoded = decodeUrl<WizardTableEncoded>(encodedUrl);

			const {
				professional_id: _p,
				service_id: _s,
				...rest
			} = decoded || {};
			const newEncodedUrl = encodeUrl({
				...rest,
				s: '2',
			});

			searchParams.set('d', newEncodedUrl);
			navigate(`?${searchParams}`, { replace: true });
			return;
		}

		// Extrai o ID do serviço diretamente do profissional selecionado
		const serviceId = (professional as any).professional_services?.[0]
			?.service?.id;

		if (!serviceId) {
			toast.error('Este profissional não possui um serviço vinculado.');
			return;
		}

		setSelectedProfessinalId(professional.id);
		setSelectedServiceId(serviceId);
		setSelectedData({
			first_name: professional.first_name,
			last_name: professional?.last_name || '',
		});

		const wizardState = readWizardState();
		const encodedUrl = encodeUrl({
			...wizardState,
			s: '2',
			professional_id: professional.id,
			service_id: serviceId,
		});

		searchParams.set('d', encodedUrl);
		navigate(`?${searchParams}`, { replace: true });
	};

	const handleNextStep = () => {
		setProfessionalSearch('');
		const wizardState = readWizardState();
		const nextParams = advanceSessionWizardStep(searchParams, {
			s: String(Number(wizardState.s ?? 2) + 1),
		});
		setSearchParams(nextParams, { replace: true });
		nextStep();
	};
	const handlePageChange = (newPage: number) => setPage(newPage);

	const handleLimitChange = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};
	const professionals =
		professionalsData?.data ?? professionalsData?.professionals ?? [];
	const hasProfessionals = professionals.length > 0;

	return (
		<section className="flex w-full flex-col items-center gap-6">
			<div className="flex w-full flex-col gap-4">
				<SessionStepTitle
					className="w-fit"
					title="Selecione um profissional:"
					icon={<Stethoscope className="w-5" />}
				/>

				<div className="w-full min-w-0">
					<SearchInput
						key={`session-wizard-professional-search-${listPanelKey}`}
						syncToUrl={false}
						value={professionalSearch}
						onValueChange={setProfessionalSearch}
						placeholder="Pesquisar por nome..."
						className="w-full"
					/>
				</div>

				<div className="w-full min-w-0">
					<Filter
						key={`session-wizard-professional-filters-${listPanelKey}`}
						filters={filters}
					/>
				</div>
			</div>

			{professionalsData && (
				<div
					ref={professionalsContainerRef}
					className="w-full space-y-2"
				>
					{hasProfessionals ? (
						<>
							<DataTable
								columns={ProfessionalColumnsSession({
									onSelect: handleProfessionalSelected,
									selectedProfessionalId,
								})}
								data={professionals}
								selectedItem={selectedProfessionalId}
								page={page}
								limit={limit}
								totalItems={professionalsData.total ?? 0}
								onPageChange={handlePageChange}
								onLimitChange={handleLimitChange}
							/>
							{selectedData && (
								<div className="flex items-end gap-1 text-sm">
									<h5 className="leading-3 text-zinc-600">
										Selecionado:
									</h5>
									<p className="font-medium leading-3 text-cyan-900">
										{formatProfessionalFullName(
											selectedData,
										)}
									</p>
								</div>
							)}
						</>
					) : !isFetching && !isError ? (
						<div className="text-gray-500">
							Nenhum profissional encontrado
						</div>
					) : null}
				</div>
			)}
			<StepButtonContainer
				disabledNextButton={
					!selectedProfessionalId || !selectedServiceId
				}
				onHandleNext={handleNextStep}
				onHandlePrevius={previousStep}
			/>
		</section>
	);
};
export { StepProfessionalTable };
